import React, { useEffect, useState } from "react";
import {
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    Typography,
    Container,
    CircularProgress,
    Alert,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { 
    getPeriods, 
    addPeriod, 
    updatePeriod, 
    deletePeriod
} from "../../api/axios";
import { useTranslation } from "react-i18next";

const Period = () => {
    const { t } = useTranslation("global");
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState(null);
    
    // Form state
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [numberOfPasses, setNumberOfPasses] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const periodsData = await getPeriods();
                const periodsArray = Array.isArray(periodsData) ? periodsData : [];
                setPeriods(periodsArray);
            } catch (err) {
                setError(t("period.errorFetching"));
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [t]);

    const formatTime = (time) => {
        if (!time) return "";
        // If time is in HH:mm:ss format, convert to HH:mm
        if (time.length === 8) {
            return time.substring(0, 5);
        }
        return time;
    };

    const handleOpenDialog = (period = null) => {
        if (period) {
            setEditingPeriod(period);
            setStart(formatTime(period.start) || "");
            setEnd(formatTime(period.end) || "");
            setNumberOfPasses(period.number_of_passes || "");
        } else {
            setEditingPeriod(null);
            resetForm();
        }
        setOpenDialog(true);
    };

    const resetForm = () => {
        setStart("");
        setEnd("");
        setNumberOfPasses("");
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPeriod(null);
        resetForm();
    };

    const formatTimeForBackend = (time) => {
        if (!time) return "";
        // If time is in HH:mm format, convert to HH:mm:ss
        if (time.length === 5) {
            return `${time}:00`;
        }
        return time;
    };

    const handleSubmit = async () => {
        if (!start.trim() || !end.trim() || !numberOfPasses.trim()) {
            setError(t("period.allFieldsRequired"));
            return;
        }

        const periodData = {
            start: formatTimeForBackend(start.trim()),
            end: formatTimeForBackend(end.trim()),
            number_of_passes: numberOfPasses.trim()
        };

        try {
            if (editingPeriod) {
                await updatePeriod(editingPeriod.id, periodData);
            } else {
                await addPeriod(periodData);
            }
            // Refresh list
            const periodsData = await getPeriods();
            const periodsArray = Array.isArray(periodsData) ? periodsData : [];
            setPeriods(periodsArray);
            handleCloseDialog();
            setError(null);
        } catch (err) {
            setError(t("period.errorSaving"));
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("period.deleteConfirm"))) {
            return;
        }

        try {
            await deletePeriod(id);
            const periodsData = await getPeriods();
            const periodsArray = Array.isArray(periodsData) ? periodsData : [];
            setPeriods(periodsArray);
            setError(null);
        } catch (err) {
            setError(t("period.errorDeleting"));
            console.error(err);
        }
    };

    return (
        <Container maxWidth="md">
            <Box
                sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#f9f9f9",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h4">
                        {t("period.period")}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {t("period.addPeriod")}
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading && <CircularProgress sx={{ display: "block", mx: "auto", mt: 2 }} />}

                {!loading && (
                    <List>
                        {!Array.isArray(periods) || periods.length === 0 ? (
                            <ListItem>
                                <ListItemText primary={t("period.noPeriodsFound")} />
                            </ListItem>
                        ) : (
                            periods.map((period) => (
                                <ListItem key={period.id} divider>
                                    <ListItemText
                                        primary={`${t("period.start")}: ${period.start} - ${t("period.end")}: ${period.end}`}
                                        secondary={`${t("period.numberOfPasses")}: ${period.number_of_passes}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => handleOpenDialog(period)}
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => handleDelete(period.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))
                        )}
                    </List>
                )}

                {/* Add/Edit Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>
                        {editingPeriod ? t("period.editPeriod") : t("period.addPeriod")}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    type="time"
                                    fullWidth
                                    label={t("period.start")}
                                    value={start}
                                    onChange={(e) => setStart(e.target.value)}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    type="time"
                                    fullWidth
                                    label={t("period.end")}
                                    value={end}
                                    onChange={(e) => setEnd(e.target.value)}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("period.numberOfPasses")}
                                    type="number"
                                    value={numberOfPasses}
                                    onChange={(e) => setNumberOfPasses(e.target.value)}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{t("period.cancel")}</Button>
                        <Button onClick={handleSubmit} variant="contained" color="primary">
                            {editingPeriod ? t("period.update") : t("period.add")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default Period;

